import {useEffect, useState} from "react";
import authService from "../../api/authService";
import {toast} from "react-toastify";
import {AxiosError} from "axios";
import {ErrorResponse} from "../../types/types";
import {
    Box,
    Button,
    CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, Tooltip, Typography
} from "@mui/material";
import { Document } from '../../types/types';
import { t } from '../../i18n/t';
import { getAdminLocale } from '../../i18n/adminLocale';

import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

// @ts-ignore
const DocumentManager = ({ userId, onClose, table }) => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [editingDocument, setEditingDocument] = useState<Document | null>(null);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [previewDocument, setPreviewDocument] = useState<Document | null>(null);

    useEffect(() => {
        const fetchDocuments = async () => {
            setIsLoading(true);
            try {
                const response = await authService.getUserDocuments(userId);
                setDocuments(response.data || response);
            } catch (error) {
                console.error('Ошибка загрузки документов:', error);
                toast.error(t('documents.loadFailed'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchDocuments();
    }, [userId]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        if (file) {
            // Проверяем что файл PDF
            if (file.type !== 'application/pdf') {
                toast.error(t('documents.pdfOnly'));
                return;
            }

            // Проверяем размер файла (10MB)
            if (file.size > 10 * 1024 * 1024) {
                toast.error(t('documents.sizeLimit'));
                return;
            }

            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.warning(t('documents.pickFile'));
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const result = await authService.uploadDocument(formData);
            setDocuments(prev => [result.data || result, ...prev]);
            setSelectedFile(null);
            setShowUploadDialog(false);
            toast.success(t('documents.uploaded'));
        } catch (error: unknown) {
            console.error('Ошибка загрузки:', error);

            let errorMessage = t('documents.uploadFailed');

            if (typeof error === 'object' && error !== null && 'isAxiosError' in error) {
                const axiosError = error as AxiosError<ErrorResponse>;
                if (axiosError.response?.data?.message) {
                    errorMessage = axiosError.response.data.message;
                } else if (axiosError.response?.data?.error) {
                    errorMessage = axiosError.response.data.error;
                } else if (axiosError.message) {
                    errorMessage = axiosError.message;
                }
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }

            toast.error(errorMessage, {
                autoClose: 5000,
                position: "top-right"
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (documentId: string) => {
        try {
            if (!window.confirm(t('documents.deleteConfirm'))) {
                return;
            }

            await authService.deleteDocument(documentId);
            setDocuments(prev => prev.filter(doc => doc.id !== documentId));
            toast.success(t('documents.deleted'));
        } catch (error) {
            console.error('Ошибка удаления:', error);
            toast.error(t('documents.deleteFailed'));
        }
    };

    const handleDownload = async (documents: Document) => {
        try {
            const response = await authService.downloadDocument(documents.id);

            // Создаем blob из ответа
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/pdf'
            });

            // Создаем URL для blob
            const url = window.URL.createObjectURL(blob);

            // Создаем временную ссылку для скачивания
            const link = document.createElement('a');
            link.href = url;
            link.download = documents.originalName; // Используем оригинальное имя
            link.style.display = 'none';

            // Добавляем в DOM и кликаем
            document.body.appendChild(link);
            link.click();

            // Очистка
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t('documents.downloading'));

        } catch (error) {
            console.error('Ошибка скачивания:', error);
            toast.error(t('documents.downloadFailed'));
        }
    };

    const handlePreview = async (document: Document) => {
        try {
            setPreviewDocument(document);
        } catch (error) {
            console.error('Ошибка предпросмотра:', error);
            toast.error(t('documents.previewFailed'));
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString(getAdminLocale() === 'en' ? 'en-US' : 'ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box>
            {isLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('documents.name')}</TableCell>
                                    <TableCell>{t('documents.size')}</TableCell>
                                    <TableCell>{t('documents.uploadedAt')}</TableCell>
                                    <TableCell>{t('documents.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documents.length > 0 ? (
                                    documents.map((doc) => (
                                        <TableRow key={doc.id}>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    title={doc.originalName}
                                                    sx={{
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {doc.originalName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {formatFileSize(doc.fileSize)}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(doc.createdAt)}
                                            </TableCell>
                                            <TableCell>


                                                <Tooltip title={t('documents.download')}>
                                                    <IconButton
                                                        onClick={() => handleDownload(doc)}
                                                        color="primary"
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title={t('common.delete')}>
                                                    <IconButton
                                                        onClick={() => handleDelete(doc.id)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            {t('documents.empty')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Диалог загрузки файла */}
                    {showUploadDialog && (
                        <Dialog open fullWidth maxWidth="sm">
                            <DialogTitle>{t('documents.uploadTitle')}</DialogTitle>
                            <DialogContent>
                                <Box mt={2} display="flex" flexDirection="column" gap={2}>
                                    <input
                                        type="file"
                                        accept=".pdf,application/pdf"
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                        id="file-upload"
                                    />
                                    <label htmlFor="file-upload">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            startIcon={<CloudUploadIcon />}
                                            fullWidth
                                        >
                                            {t('documents.chooseFile')}
                                        </Button>
                                    </label>

                                    {selectedFile && (
                                        <Box p={2} border={1} borderColor="divider" borderRadius={1}>
                                            <Typography variant="body2">
                                                <strong>{t('documents.fileLabel')}</strong> {selectedFile.name}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>{t('documents.sizeLabel')}</strong> {formatFileSize(selectedFile.size)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('documents.pdfHint')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => {
                                        setShowUploadDialog(false);
                                        setSelectedFile(null);
                                    }}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUpload}
                                    disabled={!selectedFile || isUploading}
                                    startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                >
                                    {isUploading ? t('documents.uploading') : t('documents.upload')}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}

                    {/* Предпросмотр документа */}
                    {previewDocument && (
                        <Dialog
                            open
                            fullWidth
                            maxWidth="lg"
                            fullScreen={false}
                            onClose={() => setPreviewDocument(null)}
                        >
                            <DialogTitle>
                                {t('documents.previewTitle')} {previewDocument.originalName}
                                <IconButton
                                    aria-label="close"
                                    onClick={() => setPreviewDocument(null)}
                                    sx={{ position: 'absolute', right: 8, top: 8 }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent>
                                <Box sx={{ height: '70vh', display: 'flex', justifyContent: 'center' }}>
                                    <iframe
                                        src={`/api/documents/${previewDocument.id}`}
                                        width="100%"
                                        height="100%"
                                        style={{ border: 'none' }}
                                        title={previewDocument.originalName}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => handleDownload(previewDocument)}
                                    startIcon={<DownloadIcon />}
                                >
                                    {t('documents.download')}
                                </Button>
                                <Button
                                    onClick={() => setPreviewDocument(null)}
                                >
                                    {t('common.close')}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}

                    {/* Кнопка добавления */}
                    <Box mt={2} display="flex" gap={1}>
                        <Button
                            variant="contained"
                            onClick={() => setShowUploadDialog(true)}
                            startIcon={<AddIcon />}
                        >
                            {t('documents.uploadButton')}
                        </Button>
                    </Box>

                    {/* Статистика */}
                    {documents.length > 0 && (
                        <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                            <Typography variant="body2" color="text.secondary">
                                {t('documents.stats', {
                                    count: documents.length,
                                    size: formatFileSize(documents.reduce((sum, doc) => sum + doc.fileSize, 0)),
                                })}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default DocumentManager;
